/**
 * The footer player bar's background (Gordon 2026-10-31: "footer media player
 * bg translucent blur background with full css setting for all users and bb
 * super admin controls the visitors settings and super admin settings
 * separate"). Default: the theme's background colour, half see-through, with
 * a blur. Two independent layers of CSS variables on <html>:
 *   --bb-footer-v-*  visitors — set by Super Admin (appearance settings)
 *   --bb-footer-u-*  a signed-in person's own choice (layout prefs)
 * globals.css picks the layer by html[data-signed-in], so Super Admin's own
 * footer never changes what visitors see. Each layer can add free CSS for
 * `.bb-player-bar` (declarations only).
 */

export interface FooterStyle {
  /** Background opacity 0–100. Default 55. */
  opacity?: number;
  /** Blur in px, 0–40. Default 12. */
  blur?: number;
  /** '#rrggbb', or empty = the theme's background colour. */
  color?: string;
  /** Extra CSS declarations for the bar, e.g. "border-top: 1px solid hotpink;". */
  css?: string;
}

export const FOOTER_DEFAULTS = { opacity: 55, blur: 12 } as const;

const clamp = (n: unknown, lo: number, hi: number, d: number) =>
  typeof n === 'number' && isFinite(n) ? Math.max(lo, Math.min(hi, n)) : d;

/** Declarations only: nothing that could close the rule or load anything. */
export function cleanFooterCss(css: string | undefined): string {
  return String(css || '')
    .replace(/[{}<>]/g, '')
    .replace(/@import|expression\s*\(|javascript:/gi, '')
    .slice(0, 2000)
    .trim();
}

export function footerBackground(s: FooterStyle | null | undefined): { bg: string; blur: string } {
  const a = clamp(s?.opacity, 0, 100, FOOTER_DEFAULTS.opacity) / 100;
  const blur = `${clamp(s?.blur, 0, 40, FOOTER_DEFAULTS.blur)}px`;
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(s?.color || '');
  const bg = m
    ? `rgba(${parseInt(m[1], 16)}, ${parseInt(m[2], 16)}, ${parseInt(m[3], 16)}, ${a})`
    : `hsl(var(--background) / ${a})`;
  return { bg, blur };
}

export function applyFooterStyle(scope: 'visitor' | 'user', s: FooterStyle | null | undefined): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  const p = scope === 'visitor' ? 'v' : 'u';
  const { bg, blur } = footerBackground(s);
  root.style.setProperty(`--bb-footer-${p}-bg`, bg);
  root.style.setProperty(`--bb-footer-${p}-blur`, blur);
  const id = `bb-footer-${scope}-css`;
  const decl = cleanFooterCss(s?.css);
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!decl) { el?.remove(); return; }
  if (!el) { el = document.createElement('style'); el.id = id; document.head.appendChild(el); }
  const sel = scope === 'visitor' ? 'html:not([data-signed-in="1"]) .bb-player-bar' : 'html[data-signed-in="1"] .bb-player-bar';
  el.textContent = `${sel} { ${decl} }`;
}
